import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";

const EMPTY_STATE = { status: "loading", data: null, error: null, stale: false, lastUpdatedAt: null };
const pendingReads = new WeakMap();

function sharedRead(api, key, read) {
  let requests = pendingReads.get(api);
  if (!requests) { requests = new Map(); pendingReads.set(api, requests); }
  if (requests.has(key)) return requests.get(key);
  const request = Promise.resolve(read());
  requests.set(key, request);
  const clear = () => { if (requests.get(key) === request) requests.delete(key); };
  request.then(clear, clear);
  return request;
}

export function useLiveResource(method, args = [], { refreshMs = 60_000, jitterRatio = 0.1, clearOnError = false, dedupe = false } = {}) {
  const { api, activeClient, member, sessionEmail, authStatus } = useApp();
  const requestVersion = useRef(0);
  const argsKey = JSON.stringify(args);
  const clientSlug = activeClient?.clientSlug ?? null;
  const accountKey = JSON.stringify([member?.id ?? null, member?.userId ?? null, member?.email ?? sessionEmail ?? null]);
  const sharedKey = JSON.stringify([clientSlug, accountKey, authStatus, method, argsKey]);
  const scope = useMemo(() => ({ api, argsKey, clientSlug, accountKey, authStatus, method }), [api, argsKey, clientSlug, accountKey, authStatus, method]);
  const activeScope = useRef(null);
  const [state, setState] = useState(() => ({ ...EMPTY_STATE, scope }));

  useLayoutEffect(() => {
    activeScope.current = scope;
    return () => {
      activeScope.current = null;
      // A remount or identity transition must make a fresh authorized request,
      // even when the API object survives sign-out and sign-in.
      if (dedupe) pendingReads.get(api)?.delete(sharedKey);
    };
  }, [api, dedupe, scope, sharedKey]);

  const load = useCallback(async () => {
    // Saved callbacks from an earlier workspace must not supersede its replacement.
    if (activeScope.current !== scope) return null;
    if (!clientSlug || (authStatus && authStatus !== "authenticated")) {
      const error = new Error("An authenticated client workspace is required.");
      setState({ ...EMPTY_STATE, scope, status: "error", error });
      throw error;
    }
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setState((previous) => ({ ...(previous.scope === scope ? previous : EMPTY_STATE), scope, status: "loading" }));
    try {
      if (!api || typeof api[method] !== "function") throw new Error(`Unknown dashboard resource: ${method}`);
      const requestArgs = JSON.parse(argsKey);
      const read = () => api[method](...requestArgs);
      const data = await (dedupe ? sharedRead(api, sharedKey, read) : read());
      if (activeScope.current === scope && requestVersion.current === version) setState({ scope, status: "success", data, error: null, stale: false, lastUpdatedAt: Date.now() });
      return data;
    } catch (error) {
      if (activeScope.current === scope && requestVersion.current === version) {
        const accessDenied = error?.status === 401 || error?.status === 403;
        setState((previous) => previous.scope === scope && previous.data != null && !clearOnError && !accessDenied
          ? { ...previous, status: "success", error, stale: true }
          : { ...EMPTY_STATE, scope, status: "error", error });
      }
      throw error;
    }
  }, [api, argsKey, authStatus, clearOnError, clientSlug, dedupe, method, scope, sharedKey]);

  useEffect(() => {
    load().catch(() => {});
    return () => {
      requestVersion.current += 1;
    };
  }, [load]);

  useEffect(() => {
    if (refreshMs <= 0) return undefined;
    let timer = null;
    let stopped = false;
    let refreshing = false;
    const boundedJitter = Math.max(0, Math.min(Number(jitterRatio) || 0, 0.5));
    const schedule = () => {
      if (stopped) return;
      const hidden = document.visibilityState !== "visible";
      const baseDelay = hidden ? Math.max(60_000, refreshMs * 4) : refreshMs;
      const offset = baseDelay * boundedJitter * ((Math.random() * 2) - 1);
      timer = window.setTimeout(run, Math.max(1_000, baseDelay + offset));
    };
    const run = async () => {
      if (stopped || refreshing) return;
      if (document.visibilityState !== "visible") { schedule(); return; }
      refreshing = true;
      try { await load(); } catch { /* load retains only data that is still authorized. */ }
      finally { refreshing = false; schedule(); }
    };
    const refreshVisible = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      if (document.visibilityState === "visible") run();
      else schedule();
    };
    window.addEventListener("focus", refreshVisible);
    document.addEventListener("visibilitychange", refreshVisible);
    schedule();
    return () => {
      stopped = true;
      window.removeEventListener("focus", refreshVisible);
      document.removeEventListener("visibilitychange", refreshVisible);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [jitterRatio, load, refreshMs]);

  // Mask a changed identity/request during render, before the loading effect runs.
  const current = state.scope === scope ? state : EMPTY_STATE;
  return {
    status: current.status, data: current.data, error: current.error,
    stale: current.stale, lastUpdatedAt: current.lastUpdatedAt, reload: load,
  };
}
